import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useEffect, useState } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { isIOSApp } from '@/utils/platformDetection'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { PricingPlan } from '@/components/subscriptions/PricingCard'
import platformSubscriptionsApi from '@wtf/shared/api/platformSubscriptions'
import { captureException } from '@/config/sentry'

type CheckoutSearchParams = {
    plan: PricingPlan
    returnToPath?: string
}

export const Route = createFileRoute('/(onboarding)/(pricing)/checkout')({
    validateSearch: (
        search: Record<string, unknown>
    ): CheckoutSearchParams => ({
        plan: (search.plan as PricingPlan) || 'pro',
        returnToPath:
            typeof search.returnToPath === 'string'
                ? search.returnToPath
                : undefined,
    }),
    component: CheckoutRoute,
})

function CheckoutRoute() {
    const { plan, returnToPath } = Route.useSearch()
    const navigate = useNavigate()
    const [error, setError] = useState<string | null>(null)
    const [isRedirecting, setIsRedirecting] = useState(false)

    // TEMPORARY: Phase I iOS restriction - Remove in Phase II
    // Redirect iOS users away from checkout page to comply with App Store guidelines
    // See: docs/PHASE_I_TEMPORARY_IOS_RESTRICTIONS.md
    useEffect(() => {
        if (isIOSApp()) {
            navigate({ to: '/' })
        }
    }, [navigate])

    // TEMPORARY: Phase I iOS restriction - Remove in Phase II
    // Don't render checkout page on iOS while redirecting
    if (isIOSApp()) {
        return null
    }

    useEffect(() => {
        const initiateCheckout = async () => {
            setIsRedirecting(true)
            setError(null)

            try {
                // Build return URLs
                const baseUrl = window.location.origin
                const successUrl = `${baseUrl}/checkout-return?session_id={CHECKOUT_SESSION_ID}&plan=${plan}${returnToPath ? `&returnToPath=${encodeURIComponent(returnToPath)}` : ''}`
                const cancelUrl = `${baseUrl}/pricing${returnToPath ? `?returnToPath=${encodeURIComponent(returnToPath)}` : ''}`

                // Create checkout session
                const { url } =
                    await platformSubscriptionsApi.createCheckoutSession(
                        plan.toUpperCase() as 'PRO' | 'MAX',
                        successUrl,
                        cancelUrl
                    )

                // Detect iOS devices (including PWA)
                // iOS PWAs block window.open(), so we need to use window.location.href
                const ua = navigator.userAgent || ''
                const isiOS =
                    /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream

                if (isiOS) {
                    // iOS: navigate in same window (window.open is blocked in PWA)
                    window.location.href = url
                } else {
                    // All other browsers: open in new tab only
                    // Do not use fallback to prevent double-navigation in browsers like Edge
                    window.open(url, '_blank')
                }
            } catch (err) {
                console.error('Error creating checkout session:', err)
                captureException(err, { plan, returnToPath })
                setError(
                    'Failed to initiate checkout. Please try again or contact support.'
                )
                setIsRedirecting(false)
            }
        }

        initiateCheckout()
    }, [plan, returnToPath])

    const handleBack = () => {
        navigate({
            to: '/pricing',
            search: { returnToPath, reason: undefined, preselect: undefined },
        })
    }

    if (error) {
        return (
            <section className="relative z-10 flex h-full flex-col items-center justify-center px-4">
                <div className="mx-auto max-w-md text-center">
                    <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-red-500 bg-red-500/20 backdrop-blur-md">
                        <span className="text-3xl">✕</span>
                    </div>
                    <h1 className="font-sfpro text-28 mb-3 font-semibold text-white">
                        Checkout Error
                    </h1>
                    <p className="mb-8 text-white/70">{error}</p>
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 text-white/70 hover:text-white"
                    >
                        <ArrowLeft className="h-5 w-5" />
                        Back to plans
                    </button>
                </div>
            </section>
        )
    }

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
            </div>

            <div className="flex flex-1 flex-col items-center justify-center px-2">
                <FadeInUp delay={0.3}>
                    <div className="text-center">
                        <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-white" />
                        <h1 className="font-sfpro text-28 mb-3 font-semibold text-white">
                            {isRedirecting
                                ? 'Redirecting to checkout...'
                                : 'Preparing checkout...'}
                        </h1>
                        <p className="text-sm text-white/70">
                            You'll be redirected to Stripe's secure payment page
                        </p>
                    </div>
                </FadeInUp>
            </div>

            <FadeInUp delay={0.5} className="mt-auto mb-6 px-2">
                <div className="flex items-center gap-2 pt-6">
                    <GlossyButton
                        variant="ghost"
                        className="rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                        onClick={handleBack}
                    >
                        <ArrowLeft strokeWidth={1} className="mr-1 h-5 w-5" />
                        Back
                    </GlossyButton>
                </div>
            </FadeInUp>
        </section>
    )
}
